import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Bot,
  Loader2,
  MessageCircle,
  Send,
  Sparkles,
  X,
} from "lucide-react";
import { chatApi } from "../services/phase1Api";

const QUICK_PROMPTS = [
  "What does 589 Labs do?",
  "Which offer is right for me?",
  "What's included in the free assessment?",
  "How fast can we implement?",
];

const INITIAL_MESSAGE = {
  role: "assistant",
  content:
    "Hi! I'm the 589 Labs AI Consultant. I can help you understand our services, pricing, and find the right solution for your business. What brings you here today?",
};

export default function ClawAssistantWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (open) {
      scrollToBottom();
    }
  }, [messages, open, scrollToBottom]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: "user", content: text.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const response = await chatApi.sendMessage(sessionId, text.trim());
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: response.content },
      ]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "I apologize, but I'm having trouble connecting right now. Please try again in a moment, or feel free to book a free assessment directly on our website.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <div className="fixed bottom-20 right-5 z-[60]">
      {!open ? (
        <Button
          onClick={() => setOpen(true)}
          data-testid="open-chat-btn"
          className="h-12 rounded-full bg-[var(--brand)] px-5 text-white hover:bg-[var(--brand)]"
        >
          <MessageCircle className="mr-2 h-4 w-4" />
          Talk to an AI Consultant
        </Button>
      ) : (
        <Card className="w-[420px] overflow-hidden rounded-3xl border-border bg-background/95 backdrop-blur-2xl">
          <div className="flex items-start justify-between gap-3 p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[var(--brand)]/15 text-[var(--brand)]">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <div className="text-sm font-semibold text-foreground">
                    589 Labs AI Consultant
                  </div>
                  <Badge className="rounded-full border-border bg-muted/60 text-foreground/70">
                    Live
                  </Badge>
                </div>
                <div className="mt-1 text-xs text-foreground/60">
                  Ask me about pricing, services, or next steps.
                  <button
                    type="button"
                    onClick={() => {
                      window.location.href = "/contact?intent=book";
                    }}
                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-3 py-1 text-xs font-medium text-black hover:bg-[var(--brand)]"
                  >
                    Book assessment
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="close-chat-btn"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background/60 text-foreground/70 hover:bg-muted/60"
                aria-label="Close assistant"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <Separator className="bg-border" />

          <div className="max-h-[360px] space-y-3 overflow-auto p-4">
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2">
                {QUICK_PROMPTS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => sendMessage(p)}
                    disabled={loading}
                    className="rounded-full border border-border bg-muted/60 px-3 py-1 text-xs text-foreground/80 hover:bg-muted disabled:opacity-50"
                  >
                    <Sparkles className="mr-1 inline-block h-3 w-3 text-[var(--brand)]" />
                    {p}
                  </button>
                ))}
              </div>
            )}

            {messages.map((m, idx) => (
              <div
                key={idx}
                className={
                  m.role === "user" ? "flex justify-end" : "flex justify-start"
                }
              >
                <div
                  className={[
                    "max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed",
                    /* A-01 (May-2026): chatbot text must always render white for
                       readability against both bubble backgrounds. */
                    m.role === "user"
                      ? "bg-[var(--brand)] text-white"
                      : "border border-border bg-background text-white",
                  ].join(" ")}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="flex max-w-[85%] items-center gap-2 rounded-2xl border border-border bg-background px-4 py-3 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin text-[var(--brand)]" />
                  <span className="text-foreground/60">
                    Thinking...
                  </span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <Separator className="bg-border" />

          <div className="p-4">
            <div className="flex items-center gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about pricing, feasibility, or next steps…"
                disabled={loading}
                data-testid="chat-input"
                className="h-11 rounded-xl border-border bg-background/60 text-foreground placeholder:text-foreground/50"
              />
              <Button
                type="button"
                onClick={() => sendMessage(input)}
                disabled={loading || !input.trim()}
                data-testid="send-message-btn"
                className="h-11 rounded-xl bg-[var(--brand)] px-4 text-black hover:bg-[var(--brand)] disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            <div className="mt-2 text-[11px] text-foreground/55">
              Powered by AI • Human follow-up within 30 minutes
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
